import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Stage, Layer, Group } from 'react-konva';
import { PERSPECTIVE_WARP, RECTANGLE_WARP, SMOOTH_WARP } from 'src/models/documentWarps';
import { Checkbox } from '@cimpress/react-components';
import { Provider, ReactReduxContext } from 'react-redux';
import { useLatest } from 'src/hooks/useLatest';
import Border from '../../canvas/warps/nPoint/border';
import GrabPoints from '../../canvas/warps/nPoint/grabPoints';
import { MapPoint, Point } from 'src/types/transforms';

interface Props {
    id: string;
    points: MapPoint[];
    warpType: string;
    onDragEnd: (points: Point[]) => void;
    offset: number;
    snapCoordinates: { x: number[]; y: number[] };
    img: { src: string; width: number; height: number; scaledWidth: number; scaledHeight: number };
}

const SNAP_DISTANCE = 5;

const SourcePointCanvas: React.FC<Props> = (props) => {
    const { id, points: propsPoints, warpType, offset, snapCoordinates, img } = props;

    const [guideEnable, setGuideEnable] = useState(true);
    const [points, setPoints] = useState<Point[] | undefined>(undefined);

    const containerRef = useRef<HTMLDivElement>(null);

    // `Border`/`GrabPoints`' `useDragGesture` calls mirror the latest `onDragEnd` they're given every
    // render, but that closure's own body still needs a *live* `points` at call time -- `useLatest`
    // covers that (same technique as the `pointsRef` in `nPointWarp.tsx`).
    const pointsRef = useLatest(points);

    const resizePoints = () => {
        // set grab points be on document relative to currently set source points
        let newPoints: Point[] = [];
        if (warpType === RECTANGLE_WARP) {
            // map point to point for rectangular warp
            newPoints = propsPoints.map((point) => ({
                x: point.sourceX * img.scaledWidth + offset,
                y: point.sourceY * img.scaledHeight + offset,
            }));
        } else if (warpType === PERSPECTIVE_WARP) {
            // map perspective warp points to perspective warp points
            newPoints[0] = {
                x: propsPoints[0].sourceX * img.scaledWidth + offset,
                y: propsPoints[0].sourceY * img.scaledHeight + offset,
            };
            newPoints[1] = {
                x: propsPoints[2].sourceX * img.scaledWidth + offset,
                y: propsPoints[2].sourceY * img.scaledHeight + offset,
            };
        } else {
            // map perspective warp points to smooth warp points
            newPoints[0] = {
                x: propsPoints[0].sourceX * img.scaledWidth + offset,
                y: propsPoints[0].sourceY * img.scaledHeight + offset,
            };
            newPoints[1] = {
                x: propsPoints[propsPoints.length - 1].sourceX * img.scaledWidth + offset,
                y: propsPoints[propsPoints.length - 1].sourceY * img.scaledHeight + offset,
            };
        }

        pointsRef.current = newPoints;
        setPoints(newPoints);
    };

    // Mirrors the original componentDidMount + componentDidUpdate pair: runs on mount (deps are
    // evaluated on the first render too) and again whenever either scaled dimension changes,
    // exactly matching the combined original behavior.
    useEffect(() => {
        resizePoints();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [img.scaledWidth, img.scaledHeight]);

    const snapToCoordinate = (point: Point): Point => {
        snapCoordinates.x.forEach((x: number) => {
            if (Math.abs(point.x - x) < SNAP_DISTANCE) {
                point.x = x;
            }
        });
        snapCoordinates.y.forEach((y: number) => {
            if (Math.abs(point.y - y) < SNAP_DISTANCE) {
                point.y = y;
            }
        });
        return point;
    };

    const onDragMove = (dragPoints: Point[]): void => {
        let newPoints = dragPoints;
        if (guideEnable) {
            newPoints = dragPoints.map((point) => snapToCoordinate(point));
        }
        pointsRef.current = newPoints;
        setPoints(newPoints);
    };

    const onDragEnd = () => {
        const currentPoints = pointsRef.current;
        if (warpType === PERSPECTIVE_WARP) {
            props.onDragEnd([
                currentPoints![0],
                { x: currentPoints![1].x, y: currentPoints![0].y },
                currentPoints![1],
                { x: currentPoints![0].x, y: currentPoints![1].y },
            ]);
        } else if (warpType === RECTANGLE_WARP) {
            props.onDragEnd(currentPoints!);
        } else if (warpType === SMOOTH_WARP) {
            const updatedPoints: Point[] = [];
            const dimension = Math.sqrt(propsPoints.length);
            const distanceBetweenColumns = Math.abs(currentPoints![0].x - currentPoints![1].x) / (dimension - 1);
            const distanceBetweenRows = Math.abs(currentPoints![0].y - currentPoints![1].y) / (dimension - 1);
            propsPoints.forEach((_, index) => {
                const row = Math.floor(index / dimension);
                const column = index % dimension;
                updatedPoints.push({
                    x: currentPoints![0].x + distanceBetweenColumns * column,
                    y: currentPoints![0].y + distanceBetweenRows * row,
                });
            });
            props.onDragEnd(updatedPoints);
        }
    };

    const handleGuideChange = () => {
        setGuideEnable((prev) => !prev);
    };

    let canvas: ReactElement;
    let backDrop: ReactElement;

    if (points && containerRef.current) {
        const cursorChange = (cursor: string) => (containerRef.current!.style.cursor = cursor);

        backDrop = (
            <img
                className='scene-maker-editor__background'
                src={img.src}
                width={img.scaledWidth}
                height={img.scaledHeight}
                style={{ top: offset, left: offset }}
            />
        );

        let scaledAndExpandedPoints = points;
        if (points.length === 2) {
            scaledAndExpandedPoints = [
                points[0],
                { x: points[1].x, y: points[0].y },
                points[1],
                { x: points[0].x, y: points[1].y },
            ];
        }

        // Redux workaround for React Konva: https://github.com/konvajs/react-konva/issues/311#issuecomment-536634446
        // This can be removed as soon as we upgrade React Konva to 18.2.2 or higher which requires React 18
        canvas = (
            <ReactReduxContext.Consumer>
                {({ store }) => (
                    <Stage width={img.scaledWidth + offset * 2} height={img.scaledHeight + offset * 2}>
                        <Provider store={store}>
                            <Layer>
                                <Group>
                                    <Border
                                        selected={true}
                                        points={points}
                                        scaledPoints={scaledAndExpandedPoints}
                                        scale={1}
                                        onDragMove={onDragMove}
                                        onDragEnd={onDragEnd}
                                        selectLayer={() => {}}
                                    />
                                    <Group>
                                        <GrabPoints
                                            points={points}
                                            scaledPoints={scaledAndExpandedPoints}
                                            selectPoint={() => {}}
                                            isAspectRatioConstrained={false}
                                            aspectRatio={1}
                                            scale={1}
                                            onDragMove={onDragMove}
                                            onDragEnd={onDragEnd}
                                            cursorChange={cursorChange}
                                            documentId={id}
                                            snapPoints={[]}
                                            selectedPoint={0}
                                            rotationAngle={0}
                                        />
                                    </Group>
                                </Group>
                            </Layer>
                        </Provider>
                    </Stage>
                )}
            </ReactReduxContext.Consumer>
        );
    }

    return (
        <div ref={containerRef} className='scene-maker-editor__sourcePoint_canvas'>
            {backDrop!}
            {canvas!}
            <Checkbox
                style={{ marginLeft: '10px' }}
                checked={guideEnable}
                onChange={handleGuideChange}
                label='Snap to Coordinate Enable'
            />
        </div>
    );
};

export default React.memo(SourcePointCanvas);
